import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import './CreateOptimization.css';

const CreateOptimization = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Основные данные формы
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Портфели и типы симуляций
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [simulationTypes, setSimulationTypes] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [typeParams, setTypeParams] = useState({}); // Базовые параметры
  const [samplingParams, setSamplingParams] = useState({}); // Параметры для сэмплирования

  // Параметры сэмплирования весов портфеля
  const [enableWeightSampling, setEnableWeightSampling] = useState(false);
  const [samplingTypes, setSamplingTypes] = useState([]);
  const [activeSamplingTab, setActiveSamplingTab] = useState(null);
  const [samplingTypeParams, setSamplingTypeParams] = useState({});

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Загрузка данных при монтировании
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Загружаем портфели
        const portfoliosResponse = await api.get('/portfolios');
        const fetchedPortfolios = portfoliosResponse.data.portfolios || [];
        setPortfolios(fetchedPortfolios);

        // Предвыбор портфеля из state (если пришли с Dashboard)
        const preselectedPortfolioId = location.state?.portfolioId;
        if (preselectedPortfolioId && fetchedPortfolios.length > 0) {
          const portfolio = fetchedPortfolios.find(p => p.id === preselectedPortfolioId);
          if (portfolio) {
            setSelectedPortfolio(portfolio);
          }
        } else if (fetchedPortfolios.length > 0) {
          setSelectedPortfolio(fetchedPortfolios[0]);
        }

        // Загружаем типы симуляций
        const typesResponse = await api.get('/simulation-types');
        const types = typesResponse.data.simulation_types || [];
        setSimulationTypes(types);

        // Загружаем типы сэмплирования
        const samplingResponse = await api.get('/sampling-types');
        const samplingTypesData = samplingResponse.data.sampling_types || [];
        setSamplingTypes(samplingTypesData);

        if (samplingTypesData.length > 0) {
          const firstSamplingType = samplingTypesData[0];
          setActiveSamplingTab(firstSamplingType.key);

          // Инициализируем параметры из parameters_schema с default значениями
          const initialSamplingParams = {};
          samplingTypesData.forEach(type => {
            const defaults = {};
            if (type.parameters_schema) {
              Object.keys(type.parameters_schema).forEach(paramKey => {
                const param = type.parameters_schema[paramKey];
                defaults[paramKey] = param.default !== undefined ? param.default : '';
              });
            }
            initialSamplingParams[type.key] = defaults;
          });
          setSamplingTypeParams(initialSamplingParams);
        }

        if (types.length > 0) {
          const firstType = types[0];
          setActiveTab(firstType.id);

          // Инициализируем параметры из parameters_schema
          const initialTypeParams = {};
          const initialSamplingParams = {};

          types.forEach(type => {
            // Извлекаем default_value из parameters_schema
            const defaults = {};
            if (type.parameters_schema && type.parameters_schema.parameters) {
              type.parameters_schema.parameters.forEach(param => {
                if (param.default_value !== undefined && param.default_value !== null) {
                  defaults[param.key] = param.default_value;
                }
              });
            }
            initialTypeParams[type.id] = defaults;
            initialSamplingParams[type.id] = {}; // Пустой объект для параметров сэмплирования
          });

          setTypeParams(initialTypeParams);
          setSamplingParams(initialSamplingParams);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setErrors({ loading: 'Ошибка загрузки данных' });
        setLoading(false);
      }
    };

    fetchData();
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePortfolioChange = (e) => {
    const portfolioId = e.target.value;
    const portfolio = portfolios.find(p => p.id === portfolioId);
    setSelectedPortfolio(portfolio);
  };

  const handleParamChange = (paramName, value) => {
    setTypeParams(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [paramName]: value
      }
    }));
  };

  const handleSamplingToggle = (paramName) => {
    setSamplingParams(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [paramName]: {
          enabled: !prev[activeTab]?.[paramName]?.enabled,
          values: prev[activeTab]?.[paramName]?.values || []
        }
      }
    }));
  };

  const handleSamplingValuesChange = (paramName, values) => {
    setSamplingParams(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [paramName]: {
          ...prev[activeTab]?.[paramName],
          values: values
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setErrors({ name: 'Название обязательно' });
      return;
    }

    if (!selectedPortfolio) {
      setErrors({ portfolio: 'Выберите портфель' });
      return;
    }

    if (!activeTab) {
      setErrors({ type: 'Выберите тип симуляции' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Формируем данные батча
      const batchData = {
        name: formData.name,
        description: formData.description,
        portfolio_id: selectedPortfolio.id,
        type_id: activeTab,
        initial_type_params: typeParams[activeTab] || {},
        sampler_params: {}
      };

      // Добавляем параметры сэмплирования весов если включено
      if (enableWeightSampling && activeSamplingTab) {
        batchData.sampler_params.override_assets = {
          strategy: activeSamplingTab,
          parameters: samplingTypeParams[activeSamplingTab] || {}
        };
      }

      // Добавляем параметры для сэмплирования других параметров симуляции
      const activeSamplingParams = samplingParams[activeTab] || {};

      Object.keys(activeSamplingParams).forEach(paramName => {
        const param = activeSamplingParams[paramName];
        if (param.enabled && param.values && param.values.length > 0) {
          // В будущем здесь тоже будет выбор стратегии, пока используем grid по умолчанию
          batchData.sampler_params[paramName] = {
            strategy: 'grid',
            parameters: {
              values: param.values
            }
          };
        }
      });

      // Отправляем на сервер
      const response = await api.post('/simulation-batches', batchData);

      console.log('Batch created:', response.data);

      // Возвращаемся на Dashboard с сообщением
      navigate('/', {
        state: {
          message: `Задача оптимизации "${formData.name}" успешно создана`,
          activeTab: 'optimization'
        }
      });

    } catch (error) {
      console.error('Error creating batch:', error);
      setErrors({
        submit: error.response?.data?.detail || 'Ошибка при создании задачи оптимизации'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="create-optimization">
        <div className="header">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Назад к Dashboard
          </button>
          <h1>Создание задачи оптимизации</h1>
        </div>
        <p>Загрузка...</p>
      </div>
    );
  }

  const currentType = simulationTypes.find(t => t.id === activeTab);

  return (
    <div className="create-optimization">
      <div className="header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Назад к Dashboard
        </button>
        <h1>Создание задачи оптимизации</h1>
      </div>

      <form onSubmit={handleSubmit} className="optimization-form">
        {/* Секция 1: Основные параметры */}
        <div className="form-section">
          <h2>Основные параметры</h2>

          {errors.loading && (
            <div className="error-message">{errors.loading}</div>
          )}

          <div className="form-group">
            <label htmlFor="portfolio">
              Портфель <span className="required">*</span>
            </label>
            <select
              id="portfolio"
              value={selectedPortfolio?.id || ''}
              onChange={handlePortfolioChange}
              className="form-control"
              required
            >
              {portfolios.map(portfolio => (
                <option key={portfolio.id} value={portfolio.id}>
                  {portfolio.name} (${portfolio.total_value?.toLocaleString()})
                </option>
              ))}
            </select>
            {errors.portfolio && (
              <div className="error-message">{errors.portfolio}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name">
              Название задачи <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Например: Поиск оптимальных весов"
              required
            />
            {errors.name && (
              <div className="error-message">{errors.name}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-control"
              rows="3"
              placeholder="Опишите цель оптимизации..."
            />
          </div>
        </div>

        {/* Секция 2: Сэмплирование весов портфеля */}
        <div className="form-section">
          <h2>Сэмплирование весов портфеля</h2>

          <div className="weight-sampling-container">
            <label className="checkbox-label main-toggle">
              <input
                type="checkbox"
                checked={enableWeightSampling}
                onChange={(e) => setEnableWeightSampling(e.target.checked)}
              />
              <span>Включить сэмплирование весов активов</span>
            </label>

            {enableWeightSampling && samplingTypes.length > 0 && (
              <div className="weight-sampling-params">
                {/* Вкладки типов сэмплирования */}
                <div className="simulation-tabs">
                  {samplingTypes.map(type => (
                    <button
                      key={type.key}
                      type="button"
                      className={`tab-button ${activeSamplingTab === type.key ? 'active' : ''}`}
                      onClick={() => setActiveSamplingTab(type.key)}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>

                {/* Описание текущего типа сэмплирования */}
                {(() => {
                  const currentSamplingType = samplingTypes.find(t => t.key === activeSamplingTab);
                  if (!currentSamplingType) return null;

                  return (
                    <div className="sampling-type-content">
                      {currentSamplingType.description && (
                        <p className="help-text">{currentSamplingType.description}</p>
                      )}

                      {/* Динамические параметры */}
                      <div className="weight-params-grid">
                        {Object.keys(currentSamplingType.parameters_schema || {}).map(paramKey => {
                          const param = currentSamplingType.parameters_schema[paramKey];
                          const value = samplingTypeParams[activeSamplingTab]?.[paramKey] ?? param.default ?? '';

                          return (
                            <div key={paramKey} className="form-group">
                              <label htmlFor={`sampling_${paramKey}`}>
                                {param.title}
                              </label>
                              <input
                                type={param.type === 'integer' ? 'number' : 'number'}
                                id={`sampling_${paramKey}`}
                                value={value}
                                onChange={(e) => {
                                  const newValue = param.type === 'integer'
                                    ? parseInt(e.target.value) || param.default
                                    : parseFloat(e.target.value) || param.default;
                                  setSamplingTypeParams(prev => ({
                                    ...prev,
                                    [activeSamplingTab]: {
                                      ...prev[activeSamplingTab],
                                      [paramKey]: newValue
                                    }
                                  }));
                                }}
                                className="form-control"
                                min={param.minimum}
                                max={param.maximum}
                                step={param.type === 'integer' ? 1 : 'any'}
                              />
                              {param.description && (
                                <small className="form-hint">{param.description}</small>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Информация о портфеле */}
                      {selectedPortfolio && (
                        <div className="portfolio-assets-info">
                          <h4>Активы портфеля:</h4>
                          <div className="assets-list">
                            {selectedPortfolio.assets?.map((asset, idx) => (
                              <span key={idx} className="asset-badge">
                                {asset.symbol} (текущий: {asset.percentage}%)
                              </span>
                            ))}
                          </div>
                          <p className="assets-count">
                            Всего активов: <strong>{selectedPortfolio.assets?.length || 0}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Секция 3: Параметры симуляции с сэмплированием */}
        <div className="form-section">
          <h2>Параметры симуляции и сэмплирование</h2>

          {/* Вкладки типов симуляции */}
          <div className="simulation-tabs">
            {simulationTypes.map(type => (
              <button
                key={type.id}
                type="button"
                className={`tab-button ${activeTab === type.id ? 'active' : ''}`}
                onClick={() => setActiveTab(type.id)}
              >
                {type.name}
              </button>
            ))}
          </div>

          {/* Параметры текущего типа */}
          {currentType && currentType.parameters_schema && (
            <div className="params-container">
              {(() => {
                const schema = currentType.parameters_schema;
                const groups = schema.groups || [];
                const parameters = schema.parameters || [];
                const renderedKeys = new Set();

                const renderParameterRow = (param) => {
                  const isSamplingEnabled = samplingParams[activeTab]?.[param.key]?.enabled;
                  renderedKeys.add(param.key);

                  return (
                    <div key={param.key} className="param-row">
                      <div className="param-name-col">
                        <label>
                          {param.label}
                          {param.required && <span className="required-indicator">*</span>}
                        </label>
                      </div>

                      <div className="param-value-col">
                        <input
                          type="text"
                          value={typeParams[activeTab]?.[param.key] ?? ''}
                          onChange={(e) => handleParamChange(param.key, e.target.value)}
                          className="form-control-small"
                          disabled={isSamplingEnabled}
                        />
                      </div>

                      <div className="param-sampling-col">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={isSamplingEnabled || false}
                            onChange={() => handleSamplingToggle(param.key)}
                          />
                          <span>Включить сэмплирование</span>
                        </label>

                        {isSamplingEnabled && (
                          <div className="sampling-values">
                            <input
                              type="text"
                              placeholder="Введите значения через запятую: 5, 10, 15"
                              className="form-control-small"
                              value={samplingParams[activeTab]?.[param.key]?.values?.join(', ') || ''}
                              onChange={(e) => {
                                const values = e.target.value
                                  .split(',')
                                  .map(v => v.trim())
                                  .filter(v => v);
                                handleSamplingValuesChange(param.key, values);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="params-grid">
                    <div className="params-header">
                      <div className="param-name-col">Параметр</div>
                      <div className="param-value-col">Базовое значение</div>
                      <div className="param-sampling-col">Сэмплировать</div>
                    </div>

                    {/* Render grouped parameters */}
                    {groups
                      .sort((a, b) => a.display_order - b.display_order)
                      .map(group => {
                        const groupParams = group.parameter_keys
                          .map(key => parameters.find(p => p.key === key))
                          .filter(p => p)
                          .sort((a, b) => a.display_order - b.display_order);

                        if (groupParams.length === 0) return null;

                        return (
                          <div key={group.name} className="param-group">
                            <div className="group-title">{group.name}</div>
                            {group.description && (
                              <div className="group-description">{group.description}</div>
                            )}
                            {groupParams.map(renderParameterRow)}
                          </div>
                        );
                      })}

                    {/* Render ungrouped parameters */}
                    {parameters
                      .filter(p => !renderedKeys.has(p.key))
                      .sort((a, b) => a.display_order - b.display_order)
                      .map(renderParameterRow)}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Отмена
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Создание...' : 'Создать задачу'}
          </button>
        </div>

        {errors.submit && (
          <div className="error-message">{errors.submit}</div>
        )}
      </form>
    </div>
  );
};

export default CreateOptimization;
